import React, { Component } from 'react';
import { Grid, Divider, Checkbox } from '@material-ui/core';
import PropTypes from 'prop-types';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@material-ui/icons/Add';
import IconButton from '@material-ui/core/IconButton';
import StringItemSelector from './StringItemSelector';
import i18n from '../../i18n';
import GenericDeleteModal from './GenericDeleteModal';
import EditSpecialProjectModal from './EditSpecialProjectModal';
import AddSpecialProjectModal from './AddSpecialProjectModal';
import RequireScope from '../RequireScope';
import {
  addSpecialProject as addSpecialProjectRequest,
  deleteSpecialProject as deleteSpecialProjectRequest,
  fetchSpecialProjects,
  updateSpecialProject as editSpecialProjectRequest,
} from '../../helpers/externalRequests'

class SpecialProjectsEditor extends Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedSpecialProjectOption: { label: '' },
      allSpecialProjects: null,
      allSpecialProjectsDropdown: [],
      showDeleteModal: false,
      showEditModal: false,
      showAddModal: false,
      selectedSpecialProject: null,
      seeFinishedProjects: false,
    };
  }

  toggleEditModal = () => {
    this.setState((prevState) => {
      const showEditModal = !prevState.showEditModal;
      return { showEditModal };
    });
  };

  toggleAddModal = () => {
    this.setState((prevState) => {
      const showAddModal = !prevState.showAddModal;
      return { showAddModal };
    });
  };

  toggleDeleteModal = () => {
    this.setState((prevState) => {
      const showDeleteModal = !prevState.showDeleteModal;
      return { showDeleteModal };
    });
  };

  componentDidMount = () => {
    this.getSpecialProjects();
  };

  onSpecialProjectChange = (event) => {
    const { value } = event.target;
    if (value) {
      this.setState((prevState) => ({
        selectedSpecialProjectOption: value,
        selectedSpecialProject: prevState.allSpecialProjects.find(
          (specialProject) => specialProject.id === value
        ),
      }));
    }
  };

  formatSpecialProjectDropdown = (allSpecialProjects, seeFinishedProjects) => {
    const options = [];
    allSpecialProjects.forEach((specialProject) => {
      if (
        new Date(specialProject.endDate) > new Date() ||
        seeFinishedProjects
      ) {
        options.push({
          label: specialProject.specialProjectName,
          value: specialProject.id,
        });
      }
    });
    return options;
  };

  handleSeeFinishedProjectsCheckBox = () => {
    this.setState((prevState) => {
      const seeFinishedProjects = !prevState.seeFinishedProjects;
      const allSpecialProjectsDropdown = this.formatSpecialProjectDropdown(
        prevState.allSpecialProjects,
        seeFinishedProjects
      );
      return { seeFinishedProjects, allSpecialProjectsDropdown };
    });
  };

  getSpecialProjects = async () => {
    const { seeFinishedProjects } = this.state;
    const allSpecialProjects = await fetchSpecialProjects();
    this.setState({
      allSpecialProjects: allSpecialProjects.data,
      allSpecialProjectsDropdown: this.formatSpecialProjectDropdown(
        allSpecialProjects.data,
        seeFinishedProjects
      ),
    });
  };

  addSpecialProject = async (projectName, notes, startDate, endDate) => {
    const { allSpecialProjects, allSpecialProjectsDropdown } = this.state;
    const { openSnackbar } = this.props;
    try {
      const res = await addSpecialProjectRequest(projectName, notes, startDate, endDate);
      const newSpecialProjects = allSpecialProjects;
      newSpecialProjects.push(res.data);
      const newSpecialProjectOptions = allSpecialProjectsDropdown;
      newSpecialProjectOptions.push({
        label: res.data.specialProjectName,
        value: res.data.id,
      });

      this.setState({
        showAddModal: false,
        allSpecialProjects: newSpecialProjects,
        allSpecialProjectsDropdown: newSpecialProjectOptions,
      });
      openSnackbar(i18n.t('specialProject:addSuccess'));
    } catch {
      this.setState({
        showAddModal: false,
      });
      openSnackbar(i18n.t('specialProject:addErrorMsg'));
    };
  };

  deleteSpecialProject = async (specialProjectId) => {
    const { allSpecialProjects, allSpecialProjectsDropdown } = this.state;
    const { openSnackbar } = this.props;

    try {
      await deleteSpecialProjectRequest(specialProjectId);
      // Remove the deleted special projects from the states.
      const newSpecialProjects = allSpecialProjects;
      for (let i = 0; i < newSpecialProjects.length; i++) {
        if (newSpecialProjects[i].id === specialProjectId) {
          newSpecialProjects.splice(i, 1);
        }
      }

      const newSpecialProjectOptions = allSpecialProjectsDropdown;

      for (let i = 0; i < newSpecialProjectOptions.length; i++) {
        if (newSpecialProjectOptions[i].value === specialProjectId) {
          newSpecialProjectOptions.splice(i, 1);
        }
      }

      this.setState({
        showDeleteModal: false,
        allSpecialProjects: newSpecialProjects,
        allSpecialProjectsDropdown: newSpecialProjectOptions,
        selectedSpecialProject: null,
        selectedSpecialProjectOption: { label: '' },
      });
      openSnackbar(i18n.t('specialProject:deleteSuccess'));

    } catch(error) {
      const message = error.response?.data?.message || error.message;
      this.setState({
        showDeleteModal: false,
      });
      openSnackbar(`${i18n.t('specialProject:deleteErrorMsg')} ${message}`);
    };
  };

  editSpecialProject = async (
    specialProjectId,
    specialProjectName,
    notes,
    startDate,
    endDate
  ) => {
    const { allSpecialProjects, allSpecialProjectsDropdown } = this.state;
    const { openSnackbar } = this.props;
    try {
      await editSpecialProjectRequest(specialProjectId, specialProjectName, notes, startDate, endDate);
      const newSpecialProjects = allSpecialProjects;
      for (let i = 0; i < newSpecialProjects.length; i++) {
        if (newSpecialProjects[i].id === specialProjectId) {
          const editedSpecialProject = {
            specialProjectName,
            notes,
            startDate,
            endDate,
          };
          Object.assign(newSpecialProjects[i], editedSpecialProject);
        }
      }

      const newSpecialProjectOptions = allSpecialProjectsDropdown;
      for (let i = 0; i < newSpecialProjectOptions.length; i++) {
        if (newSpecialProjectOptions[i].value === specialProjectId) {
          newSpecialProjectOptions[i].label = specialProjectName;
        }
      }

      this.setState({
        showEditModal: false,
        allSpecialProjects: newSpecialProjects,
        allSpecialProjectsDropdown: newSpecialProjectOptions,
      });
      openSnackbar(i18n.t('specialProject:editSuccess'));
    } catch {
      this.setState({
        showEditModal: false,
      });
      openSnackbar(i18n.t('specialProject:editErrorMsg'));
    };
  };

  render() {
    const {
      selectedSpecialProjectOption,
      allSpecialProjectsDropdown,
      showDeleteModal,
      showEditModal,
      showAddModal,
      selectedSpecialProject,
      seeFinishedProjects,
    } = this.state;

    let selectedId;
    let selectedProjectName;

    if (!selectedSpecialProject) {
      selectedId = 0;
      selectedProjectName = null;
    } else {
      selectedId = selectedSpecialProject.id;
      selectedProjectName = selectedSpecialProject.specialProjectName;
    }

    return (
      <Grid container>
        <Grid item xs={7}>
          <h6>{i18n.t('specialProject:manageSpecialProjects')}</h6>
          <Divider />
          <br />
        </Grid>
        <Grid item xs={7}>
          <StringItemSelector
            selectedItem={selectedSpecialProjectOption}
            items={allSpecialProjectsDropdown}
            onItemChange={this.onSpecialProjectChange}
            label={i18n.t('specialProject:chooseSpecialProject')}
            helperText={i18n.t('specialProject:specialProjectHelp')}
          />
        </Grid>
        <Grid item xs={5} />
        <Grid item xs={2}>
          <RequireScope scopes={['entities:write']}>
            <IconButton
              color="secondary"
              aria-label="add"
              onClick={this.toggleAddModal}
            >
              <AddIcon />
            </IconButton>
            <IconButton
              aria-label="edit"
              color="secondary"
              onClick={this.toggleEditModal}
            >
              <EditIcon />
            </IconButton>

            <IconButton
              aria-label="delete"
              color="secondary"
              onClick={this.toggleDeleteModal}
            >
              <DeleteIcon />
            </IconButton>
          </RequireScope>
        </Grid>
        <Grid item xs={2}>
          <h6>
            <Checkbox
              className="studioDataCheckbox"
              checked={seeFinishedProjects}
              name="seeFinishedProjects"
              onChange={this.handleSeeFinishedProjectsCheckBox}
            />
            {i18n.t('specialProject:seeFinishedSpecialProjects')}
          </h6>
        </Grid>
        <Grid item xs={7} />
        <EditSpecialProjectModal
          showEditModal={showEditModal}
          toggleEditModal={this.toggleEditModal}
          selectedValidSpecialProject={selectedSpecialProject}
          editSpecialProject={this.editSpecialProject}
        />
        <AddSpecialProjectModal
          showAddModal={showAddModal}
          toggleAddModal={this.toggleAddModal}
          addSpecialProject={this.addSpecialProject}
        />
        <GenericDeleteModal
          showModal={showDeleteModal}
          noSelectionTitle={i18n.t('specialProject:warningSpecialProjectModal')}
          confirmMessage={`${i18n.t(
            'specialProject:confirmDeleteSpecialProject'
          )}: ${selectedProjectName}?`}
          selectedId={selectedId}
          onClose={this.toggleDeleteModal}
          onDelete={this.deleteSpecialProject}
        />
      </Grid>
    );
  }
}

SpecialProjectsEditor.propTypes = {
  openSnackbar: PropTypes.func.isRequired,
};

export default SpecialProjectsEditor;
